import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import Tippy from '@tippyjs/react';
import useAxios from 'axios-hooks';
//import extenso from 'extenso';
import { flatMap, get, isNil, omit, size, trim } from 'lodash';
import uuid from 'uuid/v4';

import Confirm from 'components/Confirm';
import ErrorMessages from 'components/ErrorMessages';
import Loader from 'components/Loader';
import RadioComponent from 'components/RadioComponent';
import ShowDebug from 'components/ShowDebug';

import DocumentoUpload from 'containers/Form/aba-documentos/DocumentoUpload';
import actions from 'containers/Form/actions';
import { EXTENSAO_DOC_ESPECIFICO_VD } from 'containers/Form/Detalhe';
import { Validacao as validacao } from 'containers/VeiculosDivulgacao/Validacao';

import useMessages from 'custom-hooks/useMessages';
import useMutableState from 'custom-hooks/useMutableState';

import { API_URL } from 'environments';

import { sendApi } from 'utils/injectApi';
import { isDebug, getUpdatedToken, deepEqual } from 'utils/tools';

import Inputs from './Inputs';
import Observacoes from './Observacoes';
import Pergunta from './Pergunta';

import './index.scss';
import 'tippy.js/dist/tippy.css';

let i = 0;

const debugLog = (...args) => isDebug && console.debug('(VEICULO-DIVULGACAO-DOC)', ++i, ...args);

function DocumentoVeiculoDivulgacao({
	setShowFormulario,
	showFormulario,
	setShowConfirm,
	showConfirm,
	setPosProcessFormulario,
	posProcessFormulario,
	removeFile
}) {
	// #region hooks
	/* REDUX */
	const dispatch = useDispatch();

	/* CUSTOM HOOKS */
	const formulario = useMutableState(['licenciamento', 'formulario']);
	const [errors, setErrors] = useState({});
	const [hasErrors, setHasErrors] = useState(false);
	const { createMessage } = useMessages();
	const [definicaoDebug, setDefinicaoDebug] = useState(formulario?.formData?.data?.enquadramento?.definicao);

	useEffect(() => {
		if (!definicaoDebug) {
			setDefinicaoDebug(formulario?.formData?.data?.enquadramento?.definicao);
		}
	}, [definicaoDebug, formulario]);

	const definicao = useMemo(
		() => (isDebug ? definicaoDebug : formulario?.formData?.data?.enquadramento?.definicao),
		[definicaoDebug, formulario]
	);

	/* ESTADOS */
	const [data, setData] = useState(null);
	const [perguntasComArquivosCancelar, setPerguntasComArquivosCancelar] = useState(null);
	const [loading, setLoading] = useState(false);
	const [dimensaoAberta, setDimensaoAberta] = useState(null);
	const formRef = useRef();
	const { idForm: idFormulario, id: idFormData } = useParams();
	const [confirmaExclusaoDocumentos, setConfirmaExclusaoDocumentos] = useState(null);
	const [forceSave, setForceSave] = useState(false);

	/* QUERIES */
	const [{ data: metadata }] = useAxios(`${API_URL}/collections/metadata/${EXTENSAO_DOC_ESPECIFICO_VD}`);
	// #endregion

	// atualiza data se mudar o formulário
	useEffect(() => {
		if (metadata && !data) {
			const dd = obtemVeiculoDivulgacaoDados(formulario);
			if (dd) {
				setData(dd.dados);
			}
		}
	}, [data, formulario, metadata]);

	useEffect(() => {
		const checkSize = size(errors) === 1;
		// condicional para caso seja erro de geração do doc específico CS (faltou apenas gerá-lo)
		const findDoc = formulario?.documentos?.find(d => d.idDocumento === EXTENSAO_DOC_ESPECIFICO_VD);
		const bool = !((checkSize && !!findDoc && size(errors[findDoc.id]) > 0) || size(errors) === 0);
		if (bool !== hasErrors) {
			setHasErrors(bool);
		}
	}, [errors, formulario, hasErrors]);

	const errorsDimensoes = useMemo(() => {
		const errorsDimensoes = {};
		if (metadata) {
			metadata.vdDimensoes.forEach(dimensao => {
				dimensao.questoes.forEach(questao => {
					questao.perguntas.forEach(pergunta => {
						if (errors[pergunta.id]) {
							errorsDimensoes[dimensao.id] = ['ERRO'];
						}
						pergunta.inputs.forEach(input => {
							if (errors[input.id]) {
								errorsDimensoes[dimensao.id] = ['ERRO'];
							}
						});
						const docsNecessarios = pergunta.documentos || [];
						docsNecessarios.forEach(doc => {
							if (errors[doc.idDocumento]) {
								errorsDimensoes[dimensao.id] = ['ERRO'];
							}
						});
					});
				});
			});
		}
		return errorsDimensoes;
	}, [errors, metadata]);

	const docEspecifico = useMemo(() => obtemVeiculoDivulgacaoDocumento(formulario), [formulario]);

	const documentos = useMemo(() => formulario?.documentos, [formulario]);

	const ehNovo = useCallback(d => d.descricaoOutroDocumento && !d.idDocumentoSei, []);

	// proveniente de uma revisão (alteração de enquadramento, por exemplo)
	const ehRevisado = useCallback(d => d.complementadaEm && !d.idDocumentoSei, []);
	const ehInvalido = useCallback(
		d => {
			const substituto = ((formulario || {}).documentos || []).find(doc => doc.original === d.id);
			return d.invalido && !d.substituido && !(substituto || {}).idDocumentoSei;
		},
		[formulario]
	);
	const ehOpcionalPreenchido = useCallback(
		d => !d.obrigatorio && !d.descricaoOutroDocumento && !d.invalido && d.filename && !d.idDocumentoSei,
		[]
	);
	// const ehSubstituto = useCallback(
	// 	d => {
	// 		const original = ((formulario || {}).documentos || []).find(doc => doc.id === d.original);
	// 		return d.original && !d.idDocumentoSei && original && d.versao > original.versao;
	// 	},
	// 	[formulario]
	// );
	// const ehFromSolicitante = useCallback(d => !d.idDocumentoSei && d.fromSolicitante, []);

	const rascunhoDocsComplementacao = useMemo(
		() => !!get(formulario, 'formData.rascunhoDocsComplementacao'),
		[formulario]
	);

	const qtdInvalidos = useMemo(
		() => ((formulario || {}).documentos || []).reduce((acc, d) => (acc += ehInvalido(d) ? 1 : 0), 0),
		[ehInvalido, formulario]
	);
	const qtdNovos = useMemo(
		() => ((formulario || {}).documentos || []).reduce((acc, d) => (acc += ehNovo(d) || ehRevisado(d) ? 1 : 0), 0),
		[ehNovo, ehRevisado, formulario]
	);
	const qtdOpcionaisPreenchidos = useMemo(
		() => ((formulario || {}).documentos || []).reduce((acc, d) => (acc += ehOpcionalPreenchido(d) ? 1 : 0), 0),
		[ehOpcionalPreenchido, formulario]
	);
	const qtdPerguntasNaoRespondidas = useMemo(
		() => (get(formulario, 'formData.extraInfo') || []).reduce((acc, info) => (acc += info.naoRespondida ? 1 : 0), 0),
		[formulario]
	);

	const emComplementacao = useMemo(
		() =>
			size(get(formulario, 'formData.resultado')) === 0 &&
			!rascunhoDocsComplementacao &&
			!!get(formulario, 'formData.dataComparecimento') &&
			!get(formulario, 'formData.expirado') &&
			// size(formulario.documentos) > 0 &&
			qtdInvalidos + qtdNovos + qtdPerguntasNaoRespondidas + qtdOpcionaisPreenchidos > 0,
		[
			formulario,
			qtdInvalidos,
			qtdNovos,
			qtdOpcionaisPreenchidos,
			qtdPerguntasNaoRespondidas,
			rascunhoDocsComplementacao
		]
	);

	const retiraDocumento = useCallback(payload => dispatch(actions.retiraDocumento(payload)), [dispatch]);

	// me da um código que use alguma api publica e verifique se uma determinada data é feriado no brasil

	const temAlgumDocumento = useCallback(
		(pergunta, temSlot = false) => {
			const docsNecessarios = pergunta.documentos || [];

			const temAlgum = docsNecessarios.reduce(
				(acc, d) =>
					acc ||
					(documentos || []).find(
						documento => documento.idDocumento === d.idDocumento && (!!documento.filename || temSlot)
					),
				false
			);

			return temAlgum;
		},
		[documentos]
	);

	const verificaPerguntasSemRespostaComDocumentos = (compararComOriginal = false, temSlot = false) => {
		const dadosOriginais = obtemVeiculoDivulgacaoDados(formulario)?.dados;
		// TODO: verificar adaptações necessárias para alterações de respostas com docs anexos
		const perguntas = metadata.vdDimensoes
			.reduce(
				(acc, d) => [
					...acc,
					...d.questoes.reduce((acc2, q) => {
						let mostrarQuestao = !q.showIf || (q.showIf && showIf(data, definicao, q.showIf, `questao ${q.id}`));
						return mostrarQuestao ? [...acc2, ...q.perguntas] : [...acc2];
					}, [])
				],
				[]
			)
			.filter(p =>
				compararComOriginal && size(dadosOriginais) > 0
					? dadosOriginais[p.id] === 'nao' && data[p.id] !== 'nao'
					: data[p.id] === 'nao'
			)
			.filter(p => temAlgumDocumento(p, temSlot));
		return perguntas;
	};

	const salvar = useCallback(
		({ draft, errors, saveOnly, data: localData = data }) => {
			try {
				const execute = async data => {
					setLoading(true);
					const formId = get(formulario, 'formData.id');
					const url = `processo/${formId}/${EXTENSAO_DOC_ESPECIFICO_VD}${draft ? '?draft=true' : ''}`;
					const newData = { ...data };
					const { data: dataResponse } = await sendApi({ url, payload: { form: newData, observacoes: errors } }, false);
					return dataResponse;
				};
				execute(localData).then(dataResponse => {
					dispatch(
						actions.alteraStatusDocumento({
							documento: dataResponse.documento,
							documentoDados: dataResponse.documentoDados
						})
					);
					createMessage(`Documento salvo${draft ? ' como rascunho' : ''} com sucesso`, 5);
					setLoading(false);
					if (!saveOnly) {
						setShowFormulario(false);
					}
				});
			} catch (error) {
				createMessage('Problemas no salvamento do documento... verifique a console', 3);
				console.error('erro no servidor', error);
				setLoading(false);
			}
		},
		[createMessage, data, dispatch, formulario, setShowFormulario]
	);

	const cancelSubmit = () => {
		let perguntasComArquivos = {};
		setPerguntasComArquivosCancelar(null);
		// verifica as perguntas sem respostas com documentos ou slots criados
		const perguntas = verificaPerguntasSemRespostaComDocumentos(true, true);
		if (perguntas.length > 0) {
			perguntasComArquivos = perguntas.reduce((acc, p) => {
				acc[p.id] = p;
				return acc;
			}, {});
			setPerguntasComArquivosCancelar(perguntasComArquivos);
			setPosProcessFormulario(true);
		}
		setShowConfirm(true);
	};

	const submit = async operacao => {
		const { draft } = operacao;
		let errors = {};

		if (!forceSave) {
			errors = validacao({ metadata, data, documentos, enquadramento: definicao });
			const perguntas = verificaPerguntasSemRespostaComDocumentos();
			if (perguntas.length > 0) {
				errors = perguntas.reduce((acc, p) => {
					acc[p.id] = ['Há documentos associados a esta pergunta'];
					return acc;
				}, errors);
			}
		}

		if (forceSave && draft) {
			salvar({ draft, errors });
		} else {
			if (size(errors) > 0) {
				setDimensaoAberta('todas');
				setErrors(errors);
				setForceSave(true);
			} else {
				setErrors({});
				salvar({ draft });
			}
		}
	};

	const excluiDocumentosAssociados = useCallback(
		(pergunta, newData, salvarRequerimento = false) => {
			const docsNecessarios = pergunta.documentos || [];
			const docsNecessariosIds = docsNecessarios.map(d => d.idDocumento);
			const docsExcluir = (documentos || []).filter(d => docsNecessariosIds.includes(d.idDocumento));
			const idsDocumentos = docsExcluir.map(d => d.id);
			retiraDocumento({ idFormData, idsDocumentos });
			if (salvarRequerimento) {
				salvar({ draft: true, saveOnly: true, data: newData });
			}
		},
		[documentos, idFormData, retiraDocumento, salvar]
	);

	const obtemIdDocumento = useCallback(
		idDocumento => {
			const documento = formulario?.documentos?.find(d => d.idDocumento === idDocumento);
			return documento?.id;
		},
		[formulario]
	);

	const limparForm = useCallback(
		(data, definicao) => {
			let estruturaAutoportante = get(data, 'tecnologia-estrutura-autoportante') === 'sim';
			let estruturaFixacao = get(data, 'estrutura-fixacao') === 'sim';
			const areaEscolaInfantil = get(data, 'area-escola-infantil') === 'sim';
			const escolaInfantilMunicipal = get(data, 'area-escola-infantil') === 'sim';
			const areaInteresseCultural = get(data, 'area-interesse-cultural') === 'sim';
			const fatorMultiplicacao = get(data, 'fator-multiplicacao') === 'fator0';
			let veiculoLuminoso = get(data, 'veiculo-luminoso') === 'sim';
			let coberturaTelhado = get(data, 'cobertura-telhado') === 'sim';
			let marquise = get(data, 'marquise') === 'sim';
			const areaCondominio = get(data, 'area-condominio') === 'sim';
			const areaMacicoVegetal = get(data, 'area-macico-vegetal') === 'sim';

			if (['banner', 'faixa', 'mural'].includes(definicao)) {
				delete data['veiculo-luminoso'];
				delete data['veiculo-dupla-face'];
				veiculoLuminoso = false;
			}

			if (definicao !== 'tela') {
				delete data['tecnologia-3d'];
			}

			if (['billboard', 'totem', 'faixa', 'mural'].includes(definicao)) {
				delete data['cobertura-telhado'];
				coberturaTelhado = false;
			}

			if (['billboard', 'totem'].includes(definicao)) {
				delete data['fachada'];
				delete data['marquise'];
				delete data['empena-cega'];
				delete data['exclusivamente-residencial'];
				marquise = false;
			}

			if (definicao !== 'letreiro') {
				delete data['projecao-passeio-publico'];
			}

			if (['faixa', 'mural', 'outdoor'].includes(definicao)) {
				delete data['estrutura-autoportante'];
				estruturaAutoportante = false;
			}

			if (['faixa', 'mural', 'billboard'].includes(definicao)) {
				delete data['estrutura-fixacao'];
				estruturaFixacao = false;
			}

			if (!estruturaAutoportante && !estruturaFixacao) {
				delete data['responsavel-tecnico'];
				delete data['numero-registro'];
				delete data['email-rt'];
				delete data['cpf-cnpj-rt'];
				delete data['telefone-rt'];
				delete data['apolice-seguro-responsabilidade-civil'];
			}

			if (!areaEscolaInfantil) {
				delete data['escola-infantil-municipal'];
			}

			if (['totem', 'faixa', 'mural'].includes(definicao)) {
				delete data['altura-aresta-superior'];
				delete data['altura-aresta-inferior'];
				delete data['comprimento-horizontal'];
				delete data['projecao-horizontal'];
				delete data['quantidade-final'];
			}

			if (definicao !== 'letreiros') {
				delete data['isencaoTaxaAmbientalLetreiros'];
				delete data['area-maxima'];
			} else {
				if (areaInteresseCultural) {
					delete data['isencaoTaxaAmbientalLetreiros'];
					delete data['area-maxima'];
				}
			}

			if (!fatorMultiplicacao) {
				delete data['solicitacao_isencao'];
			}

			let idsDocumentosAExcluir = [];

			if (!veiculoLuminoso) {
				idsDocumentosAExcluir.push(obtemIdDocumento('laudo-tecnico-seguranca-transito'));
				idsDocumentosAExcluir.push(obtemIdDocumento('art-rrt-laudo-tecnico-seguranca-transito'));
			}
			if (!coberturaTelhado) {
				idsDocumentosAExcluir.push(obtemIdDocumento('laudo-forca-ventos'));
				idsDocumentosAExcluir.push(obtemIdDocumento('art-rrt-laudo-forca-ventos'));
			}
			if (!marquise) {
				idsDocumentosAExcluir.push(obtemIdDocumento('laudo-estabilidade-marquise'));
				idsDocumentosAExcluir.push(obtemIdDocumento('art-rrt-laudo-estabilidade-marquise'));
			}
			if (!estruturaAutoportante && !estruturaFixacao) {
				idsDocumentosAExcluir.push(obtemIdDocumento('art-rrt'));
				idsDocumentosAExcluir.push(obtemIdDocumento('apolice-seguro'));
			}
			if (!areaEscolaInfantil) {
				idsDocumentosAExcluir.push(obtemIdDocumento('autorizacao-escola'));
			}
			if (escolaInfantilMunicipal) {
				idsDocumentosAExcluir.push(obtemIdDocumento('direito-de-uso-escola-municipal'));
			}
			if (!areaCondominio) {
				idsDocumentosAExcluir.push(obtemIdDocumento('ata-assembleia-condominio'));
			}
			if (!areaInteresseCultural) {
				idsDocumentosAExcluir.push(obtemIdDocumento('laudo-tecnico-ausencia-conflito-area-interesse'));
				idsDocumentosAExcluir.push(obtemIdDocumento('art-rrt-laudo-tecnico-ausencia-conflito-area-interesse'));
			}
			if (!areaMacicoVegetal) {
				idsDocumentosAExcluir.push(obtemIdDocumento('laudo-tecnico-protecao-fisico-visual'));
				idsDocumentosAExcluir.push(obtemIdDocumento('art-rrt-laudo-tecnico-protecao-fisico-visual'));
			}

			idsDocumentosAExcluir = idsDocumentosAExcluir.filter(d => !isNil(d));
			if (size(idsDocumentosAExcluir) > 0) {
				retiraDocumento({ idFormData, idsDocumentos: idsDocumentosAExcluir });
			}

			return data;
		},
		[idFormData, obtemIdDocumento, retiraDocumento]
	);

	const handleChanges = useCallback(
		(value, name, pergunta, confirmado) => {
			debugLog('name: ', name, 'value: ', value);

			let procede = true;

			if (data?.[name] !== value) {
				/* 				if (pergunta) {
					// quando for alteração de "pergunta" ou quando for confirmação de exclusão de documentos
					if (!confirmado) {
						// quando form alteração de pergunta
						if (value === 'nao') {
							const temdoc = temAlgumDocumento(pergunta, true);
							if (temdoc && !confirmaExclusaoDocumentos) {
								procede = false; // impede que o valor seja alterado
								setConfirmaExclusaoDocumentos({ value, name, pergunta });
							}
						}
					}
				} */

				if (procede) {
					setForceSave(false);
					setData(oldState => {
						let newValue = { ...oldState, [name]: value };
						newValue = limparForm(newValue, definicao);
						// if (pergunta && confirmado) {
						//							excluiDocumentosAssociados(pergunta, newValue, true);
						//							setConfirmaExclusaoDocumentos(null);
						//						}
						return newValue;
					});
					const newErrors = omit(errors, [name]);
					if (!deepEqual(newErrors, errors)) {
						setErrors(old => omit(old, [name]));
					}
				}
			}
		},
		[data, definicao, errors, limparForm]
	);

	useEffect(() => {
		if (!showConfirm && !showFormulario && perguntasComArquivosCancelar && posProcessFormulario) {
			for (let p in perguntasComArquivosCancelar) {
				excluiDocumentosAssociados(perguntasComArquivosCancelar[p], null, false);
			}
			if (perguntasComArquivosCancelar) {
				setPerguntasComArquivosCancelar(null);
			}
			if (posProcessFormulario) {
				setPosProcessFormulario(false);
			}
		}
	}, [
		excluiDocumentosAssociados,
		perguntasComArquivosCancelar,
		posProcessFormulario,
		setPosProcessFormulario,
		showConfirm,
		showFormulario
	]);

	const onDrop = useCallback(
		d => (accepted, rejected) => {
			if (size(rejected) === 0 && size(accepted) === 1) {
				const file = accepted[0];
				if (file.name.length > 100) {
					setErrors({ send: ['O nome do arquivo pode ter no máximo 100 caracteres.'] });
				} else if (accepted[0].size < 100000000) {
					setErrors(omit(errors, [d.idDocumento]));
					getUpdatedToken().then(
						token => {
							const payload = {
								idFormulario,
								idFormData,
								id: d.id,
								idDocumento: d.idDocumento,
								tituloDocumento: d.tituloDocumento,
								obrigatorio: d.obrigatorio,
								extensao: d.extensao,
								ordem: d.ordem,
								docGroup: d.docGroup,
								file,
								token,
								original: d.original,
								complementadaEm: docEspecifico?.complementadaEm ? docEspecifico.complementadaEm : d.complementadaEm,
								owner: d.owner,
								versao: d.versao,
								newDoc: true,
								fromSolicitante: d.fromSolicitante,
								observacoesDocumento: d.observacoesDocumento
							};
							dispatch(actions.uploadFile(payload));
						},
						error => {
							setErrors({ send: [error] });
						}
					);
				} else {
					setErrors({ send: ['O tamanho máximo de upload é de 100MB por arquivo.'] });
				}
			} else if (size(rejected) + size(accepted) > 1) {
				setErrors({ send: ['Apenas 1 arquivo pode ser submetido em cada documento'] });
			} else {
				setErrors({
					send: [`Tipo de arquivo inválido. Selecione um arquivo do tipo ${(d.extensao || '').toUpperCase()}`]
				});
			}
		},
		[dispatch, docEspecifico, errors, idFormData, idFormulario]
	);

	const proximaOrdem = useMemo(() => {
		const maior = formulario?.documentos?.reduce((acc, o) => (o.ordem && o.ordem > acc ? o.ordem : acc), 0) || 0;
		const proxima = maior - (maior % 100) + 100;
		return proxima;
	}, [formulario]);

	const getMotivos = useCallback(
		docs =>
			docs
				.filter(d => d.complementadaEm)
				.reduce((acc, d) => {
					const original = docs.find(dAux => dAux.id === d.original);
					return {
						...acc,
						[d.id]: [
							{
								type: `${get(original, 'motivo') ? 'warning' : 'info'}`,
								message: `${
									get(original, 'motivo')
										? `Arquivo rejeitado pelo revisor: ${
												size(get(original, 'motivo')) > 0 ? get(original, 'motivo') : 'sem motivo'
										  }`
										: 'Novo arquivo complementado'
								}`,
								resolvido: d.filename
							}
						]
					};
				}, {}),
		[]
	);

	const obtemPergunta = origem => {
		let pergunta = null;
		metadata.vdDimensoes.forEach(d => {
			d.questoes.forEach(q => {
				q.perguntas.forEach(p => {
					if (p.id === origem) {
						pergunta = p;
					}
				});
			});
		});
		return pergunta;
	};

	const obtemEnquadramentoLegal = () => {
		const tecnologia3d = get(data, 'tecnologia-3d') === 'sim';
		const luminoso = get(data, 'veiculo-luminoso') === 'sim';
		const fachada = get(data, 'fachada') === 'sim';

		switch (definicao) {
			case 'billboard':
				if (!tecnologia3d && !luminoso && !fachada) {
					return 'art. 2º, inc. V, "a", da Lei n.º 13.956/24';
				}
				break;
			case 'outdoor':
				if (!tecnologia3d && !luminoso) {
					return 'art. 2º, inc. V, "a", da Lei n.º 13.956/24';
				}
				break;
			case 'tela':
				if (!tecnologia3d && luminoso && !fachada) {
					return 'art. 2º, inc. V, "a", da Lei n.º 13.956/24';
				}
				if (!tecnologia3d && luminoso && fachada) {
					return 'art. 2º, inc. V, "i", da Lei n.º 13.956/24';
				}
				if (tecnologia3d && luminoso && fachada) {
					return 'art. 2º, inc. V, "j", da Lei n.º 13.956/24';
				}
				break;
			case 'banner':
				if (!tecnologia3d && !luminoso) {
					return 'art. 2º, inc. V, "a", da Lei n.º 13.956/24';
				}
				break;
			case 'led':
				if (!tecnologia3d && luminoso) {
					return 'art. 2º, inc. V, "c", da Lei n.º 13.956/24';
				}
				break;
			case 'letreiro':
				if (!tecnologia3d) {
					return 'art. 2º, inc. V, "d", da Lei n.º 13.956/24';
				}
				break;
			case 'totem':
				if (!tecnologia3d && !fachada) {
					return 'art. 2º, inc. V, "g", da Lei n.º 13.956/24';
				}
				break;
			case 'placa':
				if (!tecnologia3d) {
					return 'art. 2º, inc. V, "b", da Lei n.º 13.956/24';
				}
				break;
			case 'faixa':
				if (!tecnologia3d) {
					return 'art. 2º, inc. V, "f", da Lei n.º 13.956/24';
				}
				break;
			case 'mural':
				if (!tecnologia3d) {
					return 'art. 2º, inc. V, "h", da Lei n.º 13.956/24';
				}
				break;
			case 'outros':
				return 'art. 2º, §7º c/c inc. V, "g", da Lei n.º 13.956/24';
			default:
				return '';
		}
	};

	return (
		<>
			{loading && <Loader />}
			<div className="form-base" ref={formRef}>
				<>
					{isDebug && (
						<>
							<RadioComponent
								label="Definição em debug"
								options={[
									{ codigo: 'billboard', descricao: 'Billboard' },
									{ codigo: 'outdoor', descricao: 'Outdoor' },
									{ codigo: 'tela', descricao: 'Tela (Ecrã)' },
									{ codigo: 'led', descricao: 'Painel Eletrônico (LED)' },
									{ codigo: 'letreiro', descricao: 'Letreiro' },
									{ codigo: 'totem', descricao: 'Totem' },
									{ codigo: 'banner', descricao: 'Banner' },
									{ codigo: 'placa', descricao: 'Placa' },
									{ codigo: 'plaqueta', descricao: 'Plaqueta' },
									{ codigo: 'faixa', descricao: 'Faixa' },
									{ codigo: 'mural', descricao: 'Mural artístico' },
									{ codigo: 'outros', descricao: 'Outros' }
								]}
								value={definicaoDebug}
								name={'definicaoDebug'}
								onChangeHandler={codigo => setDefinicaoDebug(codigo)}
							/>
						</>
					)}
					<h2 className="titulo-dimensao" style={{ marginTop: '30px', marginBottom: '30px' }}>
						Formulário Específico
					</h2>
					{metadata?.vdDimensoes?.map(dimensao => (
						<div key={dimensao.id} className="card">
							<div
								className="card-header"
								onClick={e => {
									setDimensaoAberta(size(errors) > 0 ? 'todas' : dimensaoAberta === dimensao.id ? null : dimensao.id);
									e.preventDefault();
									e.stopPropagation();
								}}
							>
								<span>
									{/* <div className="dimensao">{dimensao.dimensao}</div> */}
									{dimensao.title}{' '}
									{size(errorsDimensoes?.[dimensao.id]) > 0 && (
										<span style={{ fontWeight: '700', color: 'red' }} title="Há erros em questões desta dimensão">
											(*)
										</span>
									)}
								</span>
							</div>
							{(dimensaoAberta === dimensao.id || dimensaoAberta === 'todas') && (
								<div className="card-body">
									{dimensao.questoes
										.filter(questao => showIf(data, definicao, questao.showIf, `questao ${questao.id}`))
										.map(questao => (
											<fieldset key={questao.id}>
												{dimensao.questoes.length > 1 && size(trim(questao.subtitle)) > 0 && (
													<legend>
														{questao.subtitle}
														{questao.tooltip && (
															<>
																{' '}
																<Tippy
																	placement="bottom"
																	trigger="click"
																	hideOnClick={true}
																	content={
																		<div className="observacoes" style={{ minWidth: '500px' }}>
																			<span className="warning">{questao.tooltip}</span>
																		</div>
																	}
																>
																	<i
																		className="fa fa-info-circle"
																		style={{ color: '#fa970f', fontSize: '1.1rem', cursor: 'pointer' }}
																	/>
																</Tippy>
															</>
														)}
													</legend>
												)}
												{questao.perguntas
													.filter(pergunta => showIf(data, definicao, pergunta.showIf, `pergunta ${pergunta.id}`))
													.map(pergunta => {
														const disabled = false;
														const idsDocumentos = (pergunta.documentos || []).reduce(
															(acc, d) => [...acc, d.idDocumento],
															[]
														);
														if (pergunta.id === 'enquadramentoLegal') {
															pergunta.label =
																'Enquadramento legal para o licenciamento da mídia externa, conforme dados preenchidos anteriormente: ';
															const enquadramentoLegal = obtemEnquadramentoLegal();
															pergunta.label = pergunta.label.concat(enquadramentoLegal);
														}
														let docs = documentos.filter(d => idsDocumentos.includes(d.idDocumento));
														const motivos = getMotivos(docs);
														//		const docGroups = docs.reduce((acc, d) => [...acc, d.id], []);
														//		docs = [...docs, ...documentos.filter(d => docGroups.includes(d.docGroup))];
														docs = docs.sort((d1, d2) => (d1.ordem > d2.ordem ? 1 : -1));

														idsDocumentos.forEach((id, i) => {
															if (!docs.find(dAux => dAux.idDocumento === id)) {
																const doc = pergunta.documentos[i];
																const newDoc = Object.assign(
																	{ id: uuid(), versao: 1, ordem: proximaOrdem, extensao: 'pdf' },
																	doc
																);
																docs = [...docs, newDoc];
															}
														});

														const maioresOrdens = extrairMaioresOrdens(docs);

														return (
															<div
																key={pergunta.id}
																className={`pergunta${
																	pergunta.classNameContainer ? ` ${pergunta.classNameContainer}` : ''
																}`}
															>
																<Pergunta
																	data={data}
																	definicao={definicao}
																	pergunta={pergunta}
																	required={pergunta.required}
																	readOnly={false}
																	disabled={disabled}
																	errors={errors}
																	onChangeHandler={(value, name) => handleChanges(value, name, pergunta, false)}
																	classNameContainer={pergunta.classNameContainer || 'col-md-12'}
																/>
																{size(pergunta.inputs) > 0 && (
																	<div key={'perguntas-inputs'} className="form-group">
																		<Inputs
																			data={data}
																			errors={errors}
																			setErrors={setErrors}
																			classNameContainer={'pergunta-input'}
																			fields={pergunta.inputs.map(i => ({
																				...i,
																				obrigatorio: i.obrigatorio
																					? typeof i.obrigatorio === 'object'
																						? showIf(data, definicao, i.obrigatorio, `input ${i.id}`)
																						: i.obrigatorio
																					: false,
																				perguntaOrigem: i.origem ? obtemPergunta(i.origem) : null
																			}))}
																			onChangeHandler={handleChanges}
																			definicao={definicao}
																		/>
																	</div>
																)}
																{docs
																	.filter(d => !emComplementacao || (!d.invalido && !d.substituido))
																	.filter(d => showIf(data, definicao, d.showIf, `documento ${d.idDocumento}`))
																	.map((doc, i) => {
																		const obrigatorio = doc.obrigatorio
																			? typeof doc.obrigatorio === 'object'
																				? showIf(data, definicao, doc.obrigatorio, `input ${i.id}`)
																				: doc.obrigatorio
																			: false;

																		const docMaiorOrdem = maioresOrdens[doc.docGroup || doc.id] === doc.ordem;

																		const emProcessamento = !doc.idDocumentoSei && !!doc.originalName;
																		const arquivoGerado =
																			!!doc.filename ||
																			formulario.documentos.find(d => d.id === doc.id && d.filename) ||
																			size(get(formulario, 'formData.resultado')) > 0;
																		return (
																			<div key={doc.id} className="form-group">
																				<label htmlFor={`inputFile${doc.idDocumento}`}>
																					<span style={{ display: 'flex', justifyContent: 'space-between' }}>
																						<span>
																							{doc.tituloDocumento || doc.tituloDocumentoAdmin}
																							{obrigatorio ? <span className="required">*</span> : ''}
																							{doc.versao && doc.versao !== 1 ? ` (v. ${doc.versao})` : ''}
																						</span>
																						<div style={{ display: 'flex', alignItems: 'center' }}>
																							{isDebug && (
																								<span className="debug-message">
																									({doc.idDocumento} - {doc.ordem})
																								</span>
																							)}
																						</div>
																					</span>
																					{doc.descricaoDocumento && (
																						<div className="observacoes">
																							<span className="info">{doc.descricaoDocumento}</span>
																						</div>
																					)}
																				</label>

																				<DocumentoUpload
																					key={doc.idDocumento + i}
																					formData={formulario.formData}
																					documento={doc}
																					emProcessamento={emProcessamento}
																					arquivoGerado={arquivoGerado}
																					onDrop={onDrop}
																					removeFile={docMaiorOrdem ? removeFile : null}
																					retiraDocumento={retiraDocumento}
																					procedimentoFormatadoSei={formulario.formData.procedimentoFormatadoSei}
																					usuarioDonoTask={true}
																					emComplementacao={emComplementacao}
																					naoPodeRemoverSlot={!doc.fromSolicitante}
																				/>
																				{size(doc.observacoesDocumento) > 0 && (
																					<Observacoes
																						observacoes={doc.observacoesDocumento}
																						data={data}
																						definicao={definicao}
																						debugId={pergunta.id}
																					/>
																				)}
																				{size(motivos?.[doc.id]) > 0 && <ErrorMessages errorList={motivos[doc.id]} />}
																				{size(errors?.[doc.id]) > 0 && <ErrorMessages errorList={errors[doc.id]} />}
																				{size(errors?.[doc.idDocumento]) > 0 && (
																					<ErrorMessages errorList={errors[doc.idDocumento]} />
																				)}
																			</div>
																		);
																	})}
															</div>
														);
													})}
												{size(questao.observacoes) > 0 && (
													<Observacoes
														observacoes={questao.observacoes}
														data={data}
														definicao={definicao}
														debugId={questao.id}
													/>
												)}
											</fieldset>
										))}
								</div>
							)}
						</div>
					))}
					<div className="cjto-botoes">
						<button type="button" className="btn btn-secondary mt-3 mr-2" onClick={() => cancelSubmit()}>
							Cancelar
						</button>
						<button type="button" className="btn btn-secondary mt-3 mr-2" onClick={() => submit({ draft: true })}>
							{forceSave ? 'Ignorar e Salvar Rascunho' : 'Salvar Rascunho'}
						</button>
						{
							<button
								type="button"
								className="btn btn-primary mt-3"
								onClick={() => submit({ draft: false })}
								disabled={hasErrors}
							>
								Salvar e gerar PDF
							</button>
						}
					</div>
					{hasErrors && (
						<div className="vistoria-card erros">
							<h3 className="font-weight-bold text-danger m-3">(*) Existem erros no formulário</h3>
							{<ErrorMessages showLink={true} errorList={flatMap(errors, v => v)} />}
						</div>
					)}
					<ShowDebug data={{ data, definicao, definicaoDebug }} />
				</>
			</div>
			{confirmaExclusaoDocumentos && (
				<Confirm
					msg="Confirma exclusão de todos os documentos associados a esta pergunta?"
					onConfirm={() =>
						handleChanges(
							confirmaExclusaoDocumentos.value,
							confirmaExclusaoDocumentos.name,
							confirmaExclusaoDocumentos.pergunta,
							true
						)
					}
					loader={false}
					onReject={() => setConfirmaExclusaoDocumentos(null)}
				/>
			)}
		</>
	);
}
DocumentoVeiculoDivulgacao.displayName = 'DocumentoVeiculoDivulgacao';
DocumentoVeiculoDivulgacao.propTypes = {
	setShowFormulario: PropTypes.func,
	showFormulario: PropTypes.bool,
	setShowConfirm: PropTypes.func,
	showConfirm: PropTypes.bool,
	setPosProcessFormulario: PropTypes.func,
	posProcessFormulario: PropTypes.bool,
	readOnly: PropTypes.bool,
	disabled: PropTypes.bool,
	onChangeHandler: PropTypes.func,
	removeFile: PropTypes.func
};
export default DocumentoVeiculoDivulgacao;

export function obtemVeiculoDivulgacaoDocumento(formulario, tipo = EXTENSAO_DOC_ESPECIFICO_VD) {
	const { documentos } = formulario || {};
	const documento = (documentos || []).reduce((acc, doc) => {
		if (doc.extensao === tipo) {
			if (acc) {
				return doc.versao > acc.versao ? doc : acc;
			} else {
				return doc;
			}
		}
		return acc;
	}, null);
	return documento;
}

export function obtemVeiculoDivulgacaoDados(formulario, tipo = EXTENSAO_DOC_ESPECIFICO_VD) {
	const { documentosDados } = formulario || {};
	const documento = obtemVeiculoDivulgacaoDocumento(formulario, tipo);
	const docDados = (documentosDados || []).find(dd => dd?.id === documento?.id || dd?.id === documento.original);
	return docDados;
}

function extrairMaioresOrdens(documentos) {
	// ordenar documentos em ordem crescente com base no campo ordem
	documentos.sort((a, b) => a.ordem - b.ordem);

	// objeto para armazenar a maior ordem de cada grupo
	const maioresOrdens = {};

	// percorrer documentos em ordem crescente
	for (const documento of documentos) {
		if (!documento.docGroup) {
			// documento não tem docGroup, criar nova entrada no objeto
			maioresOrdens[documento.id] = documento.ordem;
		} else {
			// documento tem docGroup, verificar se já existe uma entrada no objeto para o docGroup
			if (maioresOrdens[documento.docGroup]) {
				// comparar campo ordem do documento com valor armazenado na entrada do docGroup
				const maiorOrdem = maioresOrdens[documento.docGroup];
				if (documento.ordem > maiorOrdem) {
					// atualizar valor armazenado na entrada do docGroup
					maioresOrdens[documento.docGroup] = documento.ordem;
				}
			} else {
				// ainda não há uma entrada no objeto para o docGroup, criar uma nova
				maioresOrdens[documento.docGroup] = documento.ordem;
			}
		}
	}

	return maioresOrdens;
}

// FUNÇÃO TEM CÓPIA NO NODE-RED => REPLICAR AO ALTERÁ-LA
export function showIf(data, definicao, showIfConditions, debug) {
	if (!showIfConditions) {
		return true;
	}

	if (Array.isArray(showIfConditions)) {
		return showIfConditions.some(item => showIf(data, definicao, item, debug));
	}

	let output = true;
	let resultadoDefinicao,
		resultadoAnd,
		resultadoOr = false;

	if (size(showIfConditions.definicao) > 0) {
		if (showIfConditions.definicao.not) {
			resultadoDefinicao = notIncludes(showIfConditions.definicao.not, definicao);
			output = output && resultadoDefinicao;
		} else {
			resultadoDefinicao = includes(showIfConditions.definicao, definicao);
			output = output && resultadoDefinicao;
		}
	}

	if (size(showIfConditions.and) > 0) {
		let aux = true;
		for (const property in showIfConditions.and) {
			const valueNecessario = showIfConditions.and[property];
			if (Array.isArray(valueNecessario)) {
				aux = aux && includes(valueNecessario, data?.[property]);
				resultadoAnd = aux;
			} else {
				if (typeof valueNecessario === 'object') {
					if (valueNecessario.neq) {
						if (Array.isArray(data?.[property])) {
							aux = aux && notIncludes(data?.[property], valueNecessario.neq);
							resultadoAnd = aux;
						} else {
							aux = aux && data?.[property]?.toLowerCase() !== valueNecessario.neq.toLowerCase();
							resultadoAnd = aux;
						}
					}
					if (valueNecessario.gt) {
						let valorAux = data?.[property] || '0';
						valorAux = valorAux.replace('.', '').replace(',', '.');
						valorAux = parseFloat(valorAux);
						aux = aux && valorAux > valueNecessario.gt;
						resultadoAnd = aux;
					}
					if (valueNecessario.lt) {
						let valorAux = data?.[property] || '0';
						valorAux = valorAux.replace('.', '').replace(',', '.');
						valorAux = parseFloat(valorAux);
						aux = aux && valorAux < valueNecessario.lt;
						resultadoAnd = aux;
					}
				} else {
					if (Array.isArray(data?.[property])) {
						aux = aux && includes(data?.[property], valueNecessario);
						resultadoAnd = aux;
					} else {
						aux = aux && data?.[property]?.toLowerCase() === valueNecessario?.toLowerCase();
						resultadoAnd = aux;
					}
				}
			}
		}
		output = output && aux;
	}

	if (size(showIfConditions.or) > 0) {
		if (!Array.isArray(showIfConditions.or)) {
			showIfConditions.or = [showIfConditions.or];
		}
		showIfConditions.or.forEach(or => {
			let aux = false;
			for (const property in or) {
				const valueNecessario = or[property];
				if (Array.isArray(valueNecessario)) {
					aux = aux || includes(valueNecessario, data?.[property]);
				} else {
					if (typeof valueNecessario === 'object') {
						if (valueNecessario.neq) {
							if (Array.isArray(data?.[property])) {
								aux = aux || notIncludes(data?.[property], valueNecessario.neq);
								resultadoOr = aux;
							} else {
								aux = aux || data?.[property]?.toLowerCase() !== valueNecessario.neq?.toLowerCase();
								resultadoOr = aux;
							}
						}
						if (valueNecessario.gt) {
							let valorAux = data?.[property] || '0';
							valorAux = valorAux.replace('.', '').replace(',', '.');
							valorAux = parseFloat(valorAux);
							aux = aux || valorAux > valueNecessario.gt;
							resultadoAnd = aux;
						}
						if (valueNecessario.lt) {
							let valorAux = data?.[property] || '0';
							valorAux = valorAux.replace('.', '').replace(',', '.');
							valorAux = parseFloat(valorAux);
							aux = aux || valorAux < valueNecessario.lt;
							resultadoAnd = aux;
						}
					} else {
						if (Array.isArray(data?.[property])) {
							aux = aux || includes(data?.[property], valueNecessario);
							resultadoOr = aux;
						} else {
							aux = aux || data?.[property]?.toLowerCase() === valueNecessario?.toLowerCase();
							resultadoOr = aux;
						}
					}
				}
			}
			output = output && aux;
		});
	}

	console.debug(`showIf (${debug})`, definicao, showIfConditions, data, {
		output,
		resultadoDefinicao,
		resultadoAnd,
		resultadoOr
	});

	return output;
}

// FUNÇÃO TEM CÓPIA NO NODE-RED => REPLICAR AO ALTERÁ-LA
const includes = (array, comparado = '') => {
	array = array.map(a => a.toLowerCase());
	comparado = Array.isArray(comparado) ? comparado.map(c => c.toLowerCase()) : comparado.toLowerCase();
	return Array.isArray(comparado) ? array.some(item => comparado.includes(item)) : array.includes(comparado);
};

// FUNÇÃO TEM CÓPIA NO NODE-RED => REPLICAR AO ALTERÁ-LA
const notIncludes = (array, comparado = '') => {
	array = array.map(a => a.toLowerCase());
	comparado = Array.isArray(comparado) ? comparado.map(c => c.toLowerCase()) : comparado.toLowerCase();
	return Array.isArray(comparado) ? !array.some(item => comparado.includes(item)) : !array.includes(comparado);
};
